// Lucian UX/UI Prompt #362 · MIT License (c) 2026 Seokwoooo · https://lucianlabs.dev/uxui-prompt/#362
import { useEffect, useLayoutEffect, useRef } from 'react';
import { tCalendar } from '../../lib/calendar-i18n';
import { displayHour12 } from './time-label';
import './DrumTimePicker.css';

/**
 * 돌아가는 드럼 시간 선택 (2026-10-16 · 사용자가 준 #362 디자인).
 *
 * 오전·오후, 시(1~12), 분(5분 단위) 세 드럼이 원통처럼 돌아가며 가운데 줄에 오는 값이 선택된다.
 * 손을 떼면 가장 가까운 칸에 맞춰 서고, 위아래 값은 원통 뒤로 넘어가듯 기울고 흐려진다.
 * 드럼에 초점을 두고 ↑/↓, 또는 칸을 눌러도 고른다.
 *
 * 값은 24시간제(h 0~23)로 주고받는다 — 오전 12 = 0시(자정), 오후 12 = 12시(정오).
 * ⚠ 분이 5분 단위가 아니면(예: 7분) 가장 가까운 칸에 «보이기만» 하고 값은 손대지 않는다 — 사용자가 돌릴 때만 바뀐다.
 */
interface DrumTimePickerProps {
  hour: number;
  minute: number;
  onChange: (hour: number, minute: number) => void;
  children?: React.ReactNode;
}

/*
 * ⭐ 2026-10-16 — 옆 달력과 같은 크기 체계로 줄였다(사용자 지적 「시간이 너무 커서 달력과 안 어울린다」).
 *   종전 원본 #362 값: 칸 36 · 드럼 216 · 여백 90. ⚠ 아래 셋과 DrumTimePicker.css 의 같은 값은 짝이다.
 */
const IH = 28;            // 칸 높이
const PAD = 84;           // 위아래 여백 — 첫/끝 칸이 가운데 줄에 올 수 있게 ((196 - 28) / 2)
const HALF = 98;          // 드럼 높이(196)의 절반
const HOURS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);
const pad2 = (n: number) => String(n).padStart(2, '0');

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

export function DrumTimePicker({ hour, minute, onChange, children }: DrumTimePickerProps) {
  const pm = hour >= 12;
  const h12 = displayHour12(hour);
  const apIdx = pm ? 1 : 0;
  const hIdx = h12 - 1;
  const mIdx = Math.min(MINUTES.length - 1, Math.round(minute / 5));

  return (
    <div className="u362">
      <div className="u362-pk">
        <span className="u362-band" aria-hidden="true" />
        <Drum
          className="ap"
          ariaLabel={tCalendar('calendar.picker.ampm')}
          labels={[tCalendar('calendar.picker.am'), tCalendar('calendar.picker.pm')]}
          index={apIdx}
          onPick={(k) => onChange((hour % 12) + (k === 1 ? 12 : 0), minute)}
        />
        <Drum
          ariaLabel={tCalendar('calendar.picker.hour')}
          labels={HOURS.map(String)}
          index={hIdx}
          onPick={(k) => onChange((HOURS[k]! % 12) + (pm ? 12 : 0), minute)}
        />
        <Drum
          ariaLabel={tCalendar('calendar.picker.minute')}
          labels={MINUTES.map(pad2)}
          index={mIdx}
          onPick={(k) => onChange(hour, MINUTES[k]!)}
        />
      </div>
      {children}
    </div>
  );
}

/** 드럼 하나 — 스크롤이 멈추면(120ms) 가운데 칸을 고른다. */
function Drum({ labels, index, onPick, ariaLabel, className }: {
  labels: string[]; index: number; onPick: (k: number) => void; ariaLabel: string; className?: string;
}) {
  const colRef = useRef<HTMLDivElement>(null);
  const idxRef = useRef(index);
  idxRef.current = index;
  const pickRef = useRef(onPick);
  pickRef.current = onPick;
  /**
   * 칸 누르기·↑/↓ 로 «가는 중»인 칸. 값은 누르는 순간 올리고, 드럼이 그 칸에 설 때까지
   * 중간 위치를 고르지 않는다(지나가는 칸이 값이 되면 안 된다). 손으로 돌리기 시작하면 놓는다.
   */
  const targetRef = useRef<number | null>(null);

  // 가운데에서 멀수록 원통 뒤로 넘어가듯 기울이고 흐리게 한다.
  const bend = () => {
    const c = colRef.current;
    if (!c) return;
    const mid = c.scrollTop + HALF;
    c.querySelectorAll<HTMLLIElement>('li').forEach((li, k) => {
      const d = (k * IH + PAD + IH / 2 - mid) / IH;
      const a = Math.max(-80, Math.min(80, d * 22));
      li.style.transform = `rotateX(${(-a).toFixed(1)}deg) translateZ(${(-Math.abs(d) * 4).toFixed(1)}px)`;
      li.style.opacity = Math.max(0.15, 1 - Math.abs(d) * 0.28).toFixed(2);
    });
  };

  // 값이 바깥에서 바뀌면(처음 열 때 · 다른 드럼이 바꿀 때) 그 칸으로 돌려 둔다.
  useLayoutEffect(() => {
    const c = colRef.current;
    if (!c) return;
    if (targetRef.current === null && Math.round(c.scrollTop / IH) !== index) c.scrollTop = index * IH;
    bend();
  }, [index]);

  useEffect(() => {
    const c = colRef.current;
    if (!c) return;
    let raf = 0;
    let t: ReturnType<typeof setTimeout> | undefined;
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(() => { raf = 0; bend(); });
      clearTimeout(t);
      t = setTimeout(() => {
        const k = Math.max(0, Math.min(labels.length - 1, Math.round(c.scrollTop / IH)));
        const target = targetRef.current;
        if (target !== null) {
          // 가는 중 — 그 칸에 섰으면 끝, 아니면 마저 보낸다.
          if (k === target) targetRef.current = null;
          else go(target);
          return;
        }
        if (k !== idxRef.current) pickRef.current(k);
      }, 120);
    };
    // 손으로 돌리기 시작하면 «가는 중»을 놓는다 — 사용자의 손이 이긴다.
    const release = () => { targetRef.current = null; };
    c.addEventListener('scroll', onScroll, { passive: true });
    c.addEventListener('wheel', release, { passive: true });
    c.addEventListener('touchstart', release, { passive: true });
    return () => {
      c.removeEventListener('scroll', onScroll);
      c.removeEventListener('wheel', release);
      c.removeEventListener('touchstart', release);
      clearTimeout(t);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [labels.length]);

  function go(k: number) {
    const c = colRef.current;
    if (!c) return;
    if (typeof c.scrollTo === 'function') c.scrollTo({ top: k * IH, behavior: reducedMotion() ? 'auto' : 'smooth' });
    else c.scrollTop = k * IH;
  }
  /** 칸 k 로 간다 — 값은 바로 올리고 드럼은 그 칸으로 돈다. */
  function choose(k: number) {
    targetRef.current = k;
    if (k !== idxRef.current) pickRef.current(k);
    go(k);
  }

  return (
    <div
      ref={colRef}
      className={`u362-col${className ? ` ${className}` : ''}`}
      tabIndex={0}
      role="listbox"
      aria-label={ariaLabel}
      onKeyDown={(e) => {
        const d = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0;
        if (!d) return;
        e.preventDefault();
        // 빠르게 여러 번 눌러도 한 칸씩 쌓이게 «가는 중»인 칸에서 센다.
        choose(Math.max(0, Math.min(labels.length - 1, (targetRef.current ?? idxRef.current) + d)));
      }}
    >
      <ul>
        {labels.map((v, k) => (
          <li
            key={v}
            role="option"
            aria-selected={k === index}
            // 칸을 눌러도 고른다.
            onClick={() => choose(k)}
          >
            {v}
          </li>
        ))}
      </ul>
    </div>
  );
}
